// components/CampaignsSentTable.jsx
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  Image,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import CampaignBadge from "../Components/Badge";
import { KYDInternalCampaign } from "@common/interfaces/KYDCampaign";
import actions from "src/actions";
import { useState } from "react";
import { findImageForCampaign } from "../utils/findImageForCampaign";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

const CampaignsSentTable = ({
  campaigns,
  invalidateCampaigns,
  permissions,
}: {
  campaigns: KYDInternalCampaign[];
  permissions?: KYDScope[];
  invalidateCampaigns: () => Promise<void>;
}) => {
  const [updatingCampaignId, setUpdatingCampaignId] = useState<string | null>(
    null
  );
  const toast = useToast();
  const navigate = useNavigate();
  const handleDuplicate = async (id: string) => {
    try {
      setUpdatingCampaignId(id);
      const { id: newCampaignId } = await actions.ADMIN.duplicateCampaign(id);
      toast({
        title: "Campaign duplicated",
        description: "Campaign duplicated successfully",
        status: "success",
      });
      navigate(`/campaigns/create?id=${newCampaignId}`);
      await invalidateCampaigns();
    } catch (error) {
      console.error("Error duplicating campaign:", error);
      toast({
        title: "Error duplicating campaign",
        description: error.message,
        status: "error",
      });
    }
    setUpdatingCampaignId(null);
  };

  const handleDuplicateToUnopens = async (id: string) => {
    try {
      setUpdatingCampaignId(id);
      const { id: newCampaignId, segment_size } =
        await actions.ADMIN.duplicateCampaign(id, { audience: "unopens" });
      toast({
        title: "Campaign duplicated",
        description: `Created a resend audience with ${segment_size} unopened contacts. You're now editing the new campaign.`,
        status: "success",
      });
      navigate(`/campaigns/create?id=${newCampaignId}`);
      await invalidateCampaigns();
    } catch (error) {
      console.error("Error sending campaign to unopens:", error);
      toast({
        title: "Could not send to unopens",
        description: error.message,
        status: "error",
      });
    }
    setUpdatingCampaignId(null);
  };

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th px={0}>
            <Text>Name</Text>
          </Th>
          <Th>
            <Text>Date</Text>
          </Th>
          <Th>
            <Text>Contacts</Text>
          </Th>
          <Th>
            <Text>Open Rate</Text>
          </Th>
          <Th>
            <Text>Click Through Rate</Text>
          </Th>
          <Th>
            <Text>Revenue</Text>
          </Th>
          <Th>
            <Text>Tickets</Text>
          </Th>
          <Th></Th>
        </Tr>
      </Thead>

      <Tbody>
        {campaigns.map((c) => {
          const image = findImageForCampaign(c);
          return (
            <Tr key={c.id}>
              <Td width="100%" minWidth={"200px"} px={0}>
                <HStack spacing="3" as={Link} to={`/campaigns/${c.id}`}>
                  <Image
                    src={image}
                    boxSize="80px"
                    objectFit="cover"
                    borderRadius="2px"
                    alt={c.name}
                    aspectRatio="1"
                  />
                  <VStack align="flex-start" spacing="1" width="100%">
                    <CampaignBadge type={c.type} variant="outline" />
                    <Link to={`/campaigns/${c.id}`}>
                      <Text
                        color={"#1A202C"}
                        fontWeight="600"
                        noOfLines={1}
                        width="100%"
                      >
                        {c.name}
                      </Text>
                    </Link>
                    <Text
                      lineHeight={"130%"}
                      fontSize="12px"
                      color="#555555"
                      noOfLines={2}
                      width="100%"
                    >
                      <Text as="span" fontWeight={"600"}>
                        Subject:
                      </Text>{" "}
                      {c.subject}
                    </Text>
                  </VStack>
                </HStack>
              </Td>
              <Td width="100%" minWidth={"200px"}>
                <VStack align="flex-start" spacing="0" width="100%">
                  {c.send_at ? (
                    <>
                      <Text>{c.display?.send_at_date}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {c.display?.send_at_time}
                      </Text>
                    </>
                  ) : (
                    <Text color="gray.500">-</Text>
                  )}
                </VStack>
              </Td>

              <Td>
                <Text>{c.display?.size}</Text>
              </Td>
              <Td>
                <Text>{c.display?.open_rate}</Text>
              </Td>
              <Td>
                <Text>{c.display?.click_through_rate}</Text>
              </Td>
              <Td>
                <Text>{c.display?.revenue}</Text>
              </Td>
              <Td>
                <Text>{c.display?.tickets}</Text>
              </Td>

              <Td>
                <ShowIfHasScope
                  permissions={permissions}
                  checkFor={["crm/campaigns:write", "crm/write"]}
                >
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      size="sm"
                      isLoading={updatingCampaignId === c.id}
                      borderRadius="6px"
                      border="1px solid #A0AEC0"
                      bg="#FFF"
                    >
                      <ChevronDownIcon color="#718096" width="16px" />
                    </MenuButton>
                    <MenuList minW="0" w="240px" p={"12px"} fontSize={"13px"}>
                      <MenuItem onClick={() => handleDuplicate(c.id)}>
                        Duplicate Email Campaign
                      </MenuItem>
                      {c.status === "COMPLETED" && (
                        <MenuItem
                          onClick={() => handleDuplicateToUnopens(c.id)}
                        >
                          Send To Unopens
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </ShowIfHasScope>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

export default CampaignsSentTable;
